// What a card is ABOUT, in a form that survives the corpus it was learned from.
//
// Progress used to be keyed `(kind, index-into-vocab.words)`. That index is not a property of the
// word; it is a property of the file the word happened to be in, and it means nothing outside it.
// Two things move it:
//
//   A SWAP. Install a different pair and the same row changes meaning. Measured during the MYN-104
//   spike, on a real database: word:7 was 有 (to have) and became "this"; word:42 was 让 and became
//   "big"; word:300 was 穿 and became "difficult". Nothing errors. The scheduler reports three
//   English words due, carrying the interval history of three Chinese ones.
//
//   A REBUILD. The decks are ordered by corpus frequency, so regenerating the vocabulary from a
//   newer count shifts every index after the first word that moved — inside the SAME pair, with no
//   swap at all.
//
// So storage is keyed on the item itself, and on which pair it belongs to. Everything ABOVE
// storage — the scheduler, the decks, the API — goes on speaking in indices, because an index is
// the right currency for "the 300th word of this deck" and it costs nothing while the corpus is
// loaded. This file is the translation, and the Store implementations are the only callers.

import type { Pair } from './pair.js';
import type { ItemKind, Sentences, Vocab } from './types.js';

/**
 * Which pair a row belongs to.
 *
 * Built from the pair's own declaration rather than from the directory it was generated in, because
 * a corpus installed on a phone arrives as files with no path to read: `pair.json` travels with it
 * and the directory does not. The full tags, not the primary subtags — Mandarin explained in
 * English and Cantonese explained in English are different courses, and so are zh-CN and zh-TW.
 */
export function pairId(pair: Pair): string {
  return `${pair.learning.code}/${pair.explainedIn.code}`;
}

/**
 * A word's key: the headword itself, except for a letter.
 *
 * There is nothing more stable to use — a word has no external id the way a sentence does — and it
 * is what `unknownWordCounts` has always treated as a word's identity. test/item-key.test.ts
 * asserts the shipped corpora have no two words with the same one, which is the assumption this
 * rests on.
 *
 * AND A LETTER IS NOT THE WORD IT SPELLS. The alphabet decks put all 92 kana in the Japanese
 * vocabulary as words, and 19 of them are spelled exactly like a word the course already teaches:
 * は is both the topic particle (word 1) and the letter that makes the sound "ha"; に, の, を, と,
 * か, よ, も, ね, お, な and へ are the same story, and they are the commonest words in the
 * language. Keyed on the spelling alone the two cards shared one row of progress, so learning the
 * alphabet marked the twelve commonest words known — to somebody who had learned that a shape
 * makes a sound. It was worse than a tie: `wordIds` is built first-wins, so the letter's own index
 * was unreachable by key and anything written for it resolved back to the particle.
 *
 * ONLY THE LETTERS ARE NAMESPACED, so every other key in every course is the string it always was
 * and no existing progress moves. Rows already written against one of those 19 spellings stay with
 * the WORD, which is the card somebody is far likelier to have been studying — は at rank 1 against
 * a letter added in MYN-193 (MYN-207).
 */
export const wordKey = (word: { s: string; pos?: string }): string =>
  word.pos === 'hiragana' || word.pos === 'katakana' ? `letter:${word.s}` : word.s;

/**
 * A sentence's key: its Tatoeba id.
 *
 * The text will not do. `en-zh` has 900 sentences and 872 distinct ones — the same English sentence
 * appears with different Chinese translations, which is the whole point of a translation corpus and
 * would silently merge two cards into one. `tid` is assigned outside this project and is stable
 * across every rebuild.
 */
export const sentenceKey = (sentence: { tid: number }): string => String(sentence.tid);

/** The translation between what the app counts in and what storage keys on. */
export interface ItemKeys {
  /** The pair every row written through this belongs to. */
  readonly pair: string;
  /** The stable key for an index, or null when the corpus has no such item. */
  keyOf(kind: ItemKind, id: number): string | null;
  /** The index for a stable key, or null when this corpus does not contain it. */
  idOf(kind: ItemKind, key: string): number | null;
}

/**
 * Build the translation for one corpus.
 *
 * MEMOISED ON THE VOCABULARY, which the server loads once and hands out by reference. Without it
 * this would build two maps of sixteen thousand entries on every API call, to answer questions
 * about a handful of cards.
 */
const cache = new WeakMap<Vocab, Map<string, ItemKeys>>();

export function itemKeys(pair: Pair, vocab: Vocab, sentences: Sentences): ItemKeys {
  const id = pairId(pair);
  let byPair = cache.get(vocab);
  if (!byPair) {
    byPair = new Map();
    cache.set(vocab, byPair);
  }
  const hit = byPair.get(id);
  if (hit) return hit;

  const wordKeys = vocab.words.map(wordKey);
  const sentenceKeys = sentences.sentences.map(sentenceKey);
  const wordIds = new Map(wordKeys.map((k, i) => [k, i]));
  const sentenceIds = new Map(sentenceKeys.map((k, i) => [k, i]));

  const built: ItemKeys = {
    pair: id,
    keyOf: (kind, index) => (kind === 'word' ? wordKeys[index] : sentenceKeys[index]) ?? null,
    idOf: (kind, key) => (kind === 'word' ? wordIds.get(key) : sentenceIds.get(key)) ?? null,
  };
  byPair.set(id, built);
  return built;
}
